import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
//import 'bootstrap/dist/css/bootstrap.min.css';
import './ExamScheduleList.css';
import CustomSelect from '../../modules/custom-select/CustomSelect';
import Swal from 'sweetalert2';
import { loadFontAsBase64, loadImageAsBase64 } from '../utils/pdfUtils';

const API_URL = process.env.REACT_APP_API_URL;
const GET_TEACHERS_URL = `${API_URL}/Custom/GetAssignableUsers`;
const GET_BUILDINGS_URL = `${API_URL}/Custom/GetBuildings`;
const GET_CLASSROOMS_URL = `${API_URL}/Custom/GetRooms`;
const GET_SCHEDULE_URL = `${API_URL}/Custom/GetSchedule`;
const UPDATE_ASSIGNMENT_URL = `${API_URL}/Custom/UpdateAssignment`;
const GET_PERIODS_URL = `${API_URL}/Custom/GetExamPeriods`;
const GET_TIME_SLOTS_URL = `${API_URL}/Custom/GetTimeSlots`;
const GET_ALL_ASSIGNMENTS_URL = `${API_URL}/Custom/GetAllAssignments`;
const GET_STUDENTS_BY_ROOM_SLOT = `${API_URL}/Custom/GetStudentsByRoomAndSlot`;
const CELL_WIDTH = 250; // her “gün” sütunu 150px
const FIRST_COLUMN = 120; // solundaki başlık sütunu hâlâ 120px


export default function TeachingScheduleComponent() {
    const [mode, setMode] = useState('teacher');
    const [examPeriods, setExamPeriods] = useState([]);
    const [selectedExamPeriod, setSelectedExamPeriod] = useState(null);
    const [entities, setEntities] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [teachers, setTeachers] = useState([]);
    const [timeSlots, setTimeSlots] = useState([]);
    const [distinctTimes, setDistinctTimes] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [currentWeek, setCurrentWeek] = useState(0);
    const [schedule, setSchedule] = useState([]);
    const [savedCells, setSavedCells] = useState(new Set()); // "<slotId>-<classroomId>-<roleId>"
    const [allAssignments, setAllAssignments] = useState([]);  // examPeriod’daki Tümü

    // Fetch allAssignments whenever examPeriod changes
    useEffect(() => {
        if (!selectedExamPeriod) {
            setAllAssignments([]);
            return;
        }
        axios.get(`${GET_ALL_ASSIGNMENTS_URL}?examPeriodId=${selectedExamPeriod}`)
            .then(r => setAllAssignments(r.data || []))
            .catch(console.error);
    }, [selectedExamPeriod]);

    // Fetch schedule for selected classroom or teacher
    useEffect(() => {
        if (!selectedId || !selectedExamPeriod) {
            setSchedule([]);
            return;
        }
        const param = mode === 'teacher' ? 'teacherId' : 'classroomId';
        axios.get(`${GET_SCHEDULE_URL}?examPeriodId=${selectedExamPeriod}&${param}=${selectedId}`)
            .then(r => setSchedule(r.data || []))
            .catch(console.error);
    }, [selectedId, selectedExamPeriod, mode]);

    // Show Sat/Sun only if slots exist
    const visibleDays = useMemo(() => {
        if (!weeks.length) return [];
        return weeks[currentWeek].filter(dateStr => {
            const d = new Date(dateStr).getDay();
            if (d === 6 || d === 0) {
                return timeSlots.some(ts => ts.exactDate.startsWith(dateStr));
            }
            return true;
        });
    }, [weeks, currentWeek, timeSlots]);

    // Fetch teachers once
    useEffect(() => {
        axios.get(GET_TEACHERS_URL)
            .then(r => setTeachers(r.data || []))
            .catch(console.error);
    }, []);

    // Fetch exam periods
    useEffect(() => {
        axios.get(GET_PERIODS_URL)
            .then(r => setExamPeriods(r.data || []))
            .catch(console.error);
    }, []);

    // Reset when mode changes
    useEffect(() => {
        setSelectedBuilding(null);
        setSelectedId(null);
        setEntities([]);
        setSchedule([]);
        if (mode === 'teacher') {
            setEntities(teachers);
        } else {
            axios.get(GET_BUILDINGS_URL)
                .then(r => setBuildings(r.data || []))
                .catch(console.error);
        }
    }, [mode, teachers]);

    // Fetch classrooms when building is selected
    useEffect(() => {
        if (mode === 'classroom' && selectedBuilding) {
            axios.get(`${GET_CLASSROOMS_URL}?buildingId=${selectedBuilding}`)
                .then(r => setEntities(r.data || []))
                .catch(console.error);
        }
    }, [mode, selectedBuilding]);

    // Fetch time slots and build weeks when examPeriod changes
    useEffect(() => {
        if (!selectedExamPeriod) return;
        axios.get(`${GET_TIME_SLOTS_URL}?examPeriodId=${selectedExamPeriod}`)
            .then(res => {
                const slots = res.data || [];
                setTimeSlots(slots);

                const times = Array.from(new Set(
                    slots.map(ts => `${ts.startTime}-${ts.endTime}`)
                )).map(str => {
                    const [startTime, endTime] = str.split('-');
                    return { startTime, endTime };
                }).sort((a, b) => a.startTime.localeCompare(b.startTime));
                setDistinctTimes(times);

                const dates = Array.from(new Set(
                    slots.map(ts => ts.exactDate.split('T')[0])
                )).sort();
                if (!dates.length) {
                    setWeeks([]);
                    setCurrentWeek(0);
                    return;
                }
                const minD = new Date(dates[0]);
                const maxD = new Date(dates[dates.length - 1]);
                const firstMon = new Date(minD);
                firstMon.setDate(minD.getDate() - ((minD.getDay() + 6) % 7));
                const lastSun = new Date(maxD);
                lastSun.setDate(maxD.getDate() + (6 - ((maxD.getDay() + 6) % 7)));

                const wks = [];
                for (let w = new Date(firstMon); w <= lastSun; w.setDate(w.getDate() + 7)) {
                    const wk = [];
                    for (let i = 0; i < 7; i++) {
                        const d = new Date(w);
                        d.setDate(w.getDate() + i);
                        wk.push(d.toISOString().split('T')[0]);
                    }
                    wks.push(wk);
                }
                setWeeks(wks);
                setCurrentWeek(0);
            })
            .catch(console.error);
    }, [selectedExamPeriod]);

    const handleInlineUpdate = async (slotId, classroomId, roleId, newHeadId, newInvIds) => {
        // 1) Optimistic UI update
        setSchedule(prev =>
            prev.map(s => {
                if (s.slotId === slotId && s.classroomId === classroomId) {
                    if (roleId === 1) {
                        return { ...s, headId: newHeadId };
                    }
                    if (roleId === 2) {
                        return { ...s, invigilatorsIds: newInvIds };
                    }
                }
                return s;
            })
        );
        // Mark as saved
        const key = `${slotId}-${classroomId}-${roleId}`;
        setSavedCells(prev => new Set(prev).add(key));

        try {
            await axios.post(UPDATE_ASSIGNMENT_URL, {
                examPeriodId: selectedExamPeriod,
                timeSlotId: slotId,
                classroomId,
                roleId,
                headId: newHeadId,
                invigilatorIds: newInvIds
            });
            // success
        } catch (err) {
            console.error(err);
            // Optionally rollback by re-fetching schedule
        }
    };

    function drawHeader(
        doc: jsPDF,
        pageWidth: number,
        margin: number,
        logoDataUrl: string,
        exam: any            // tek ClassroomStudentListDto
    ) {
        const logoX = 40,
            logoY = 20,
            logoW = 80,
            logoH = 80;

        // Logo + font
        doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoW, logoH);
        doc.setFont('Roboto', 'normal');

        // Kurum adı
        doc.setFontSize(18);
        const uni = 'İzmir Tınaztepe Üniversitesi';
        doc.text(uni, (pageWidth - doc.getTextWidth(uni)) / 2, 40);

        doc.setFontSize(14);
        const center = 'Ölçme Merkezi';
        doc.text(center, (pageWidth - doc.getTextWidth(center)) / 2, 60);

        // Başlık satırları
        let y = logoY + logoH + 20;
        doc.setFontSize(12);
        const lines = [
            `Yer / Zaman : ${exam.examInfo}`,
            `Ders        : ${exam.courseInfo}`,
            `Açıldığı Bölüm : ${exam.departmentName}`,
            `Sınav Dönemi  : ${exam.examPeriod}`,
            `Ders Sorumlusu: ${exam.courseLecturer}`,
            `Salon Başkanı : ${exam.leadName}`,
            `Gözetmenler   : ${exam.guards}`
        ];
        lines.forEach(txt => { doc.text(txt, margin, y); y += 20; });

        return y + 10;            // tabloya başlanacak y
    }
    async function handleDownloadPDF(slotId: number, classroomId: number) {
        try {
            /* 1) API çağrısı */
            const res = await axios.get(
                `${GET_STUDENTS_BY_ROOM_SLOT}?classroomId=${classroomId}&timeSlotId=${slotId}`
            );
            const exams = res.data;          // artık Liste
            console.log(exams);
            /* 2) Ortak PDF kurulumları */
            const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
            const robotoBase64 = await loadFontAsBase64('/fonts/Roboto-Regular.ttf');
            doc.addFileToVFS('Roboto-Regular.ttf', robotoBase64);
            doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');

            const logoBase64 = await loadImageAsBase64('/media/logos/gradeurexam2.png');
            const logoDataUrl = `data:image/png;base64,${logoBase64}`;
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 40;

            /* 3) Her sınav için bir sayfa/sayfalar */
            exams.forEach((exam, idx) => {
                if (idx > 0) doc.addPage();              // yeni sınavsa yeni sayfa

                /* üst bilgileri çiz ve tablo başlangıç Y’sini al */
                const startY = drawHeader(doc, pageWidth, margin, logoDataUrl, exam);

                /* öğrencileri Base64’e çevir */
                const students = exam.students.map((s: any) => ({
                    ...s,
                    base64: (() => {
                        if (!s.imageData) return '';
                        if (typeof s.imageData === 'string') return s.imageData;
                        const u8 = new Uint8Array(s.imageData);
                        let bin = ''; u8.forEach(b => (bin += String.fromCharCode(b)));
                        return btoa(bin);
                    })()
                }));

                console.log(students);
                /* tablo */
                autoTable(doc, {
                    startY,
                    head: [[
                        'Fotoğraf', 'Birim', 'Ders Bilgisi',
                        'Adı Soyadı', 'Numarası', 'Sıra No', 'İmza'
                    ]],
                    body: students.map(stu => [
                        '', stu.studentOrganization, stu.courseInfo,
                        stu.fullName, stu.number, stu.seatNumber, ''
                    ]),
                    bodyStyles: { minCellHeight: 42, valign: 'middle' },
                    columnStyles: { 0: { minCellWidth: 50, maxCellWidth: 60 } },
                    styles: { font: 'Roboto', fontSize: 11 },
                    headStyles: { fillColor: [200, 200, 200] },

                    /* Her otomatik yeni sayfada da başlığı bas */
                    didDrawPage: () => {
                        // autoTable yeni sayfa açtıysa tekrar başlık çiz
                        /*if (doc.internal.getNumberOfPages() > idx + 1) {
                            drawHeader(doc, pageWidth, margin, logoDataUrl, exam);
                        }*/
                    },
                    /* Fotoğraflar */
                    didDrawCell: dataCell => {
                        if (dataCell.column.index === 0 && dataCell.cell.section === 'body' &&
                            dataCell.row.index >= 0) {
                            const i = dataCell.row.index;
                            console.log(i);
                            console.log(students[i]);
                            const img64 = students[i].base64;
                            if (img64) {
                                doc.addImage(
                                    `data:image/png;base64,${img64}`, 'PNG',
                                    dataCell.cell.x + 2, dataCell.cell.y + 2, 30, 40
                                );
                            }
                        }
                    }
                });
            });

            /* 4) Dosya adı — slot + sınıf yeterli */
            doc.save(`YoklamaFormu_Slot${slotId}_Room${classroomId}.pdf`);
        } catch (err) {
            console.error(err);
            Swal.fire({ icon: 'error', title: 'Hata', text: 'PDF oluşturulurken hata.' });
        }
    }


    const renderCell = (date, time) => {
        // 1) Find the slot for this date/time
        const slot = timeSlots.find(ts =>
            ts.exactDate.startsWith(date) &&
            ts.startTime === time.startTime &&
            ts.endTime === time.endTime
        );
        if (!slot) return <div className="cell-content empty" />;

        // 2) Get schedule items for this classroom + slot
        const items = schedule.filter(s =>
            s.date.startsWith(date) && s.slotId === slot.id
        );
        if (!items.length) return <div className="cell-content empty" />;

        // 3) TEACHER MODE
        if (mode === 'teacher') {
            if (items.length > 1) {
                const conflictList = items.map(it => {
                    const roleLabel = it.isOff
                        ? 'Görevli / İzinli'
                        : (it.headId ? 'Salon Başkanı' : 'Gözetmen');
                    return `<li>${it.examName || '—'} &ndash; <strong>${roleLabel}</strong></li>`;
                }).join('');

                Swal.fire({
                    icon: 'warning',
                    title: 'Görev Çakışması',
                    html: `
                        <p>${date} ${time.startTime.substring(0, 5)}-${time.endTime.substring(0, 5)} tarihinde birden fazla kayıt tespit edildi:</p>
                        <ul style="text-align:left; margin-left:1em;">
                            ${conflictList}
                        </ul>
                    `
                });
            }

            const it = items[0];
            const key = `${it.slotId}-${it.classroomId}-${it.roleId}`;
            const cellClass = `cell-content ${it.isOff ? 'off' : 'assigned'} ${savedCells.has(key) ? 'saved' : ''}`;

            return (
                <div className={cellClass}>
                    {it.isOff
                        ? <em>Görevli / İzinli</em>
                        : (
                            <>
                                <strong className="small">
                                    {it.roleName || (it.headId ? 'Salon Başkanı' : 'Gözetmen')}
                                </strong>
                                <span className="small">{it.examName}</span>
                            </>
                        )
                    }
                </div>
            );
        }

        // 4) CLASSROOM MODE
        const item = items[0];
        const headKey = `${slot.id}-${item.classroomId}-1`;
        const invKey = `${slot.id}-${item.classroomId}-2`;
        const isSaved = savedCells.has(headKey) || savedCells.has(invKey);

        // 5) Determine conflicting teacher IDs from allAssignments
        const conflictTeacherIds = new Set();
        allAssignments.forEach(a => {
            if (a.slotId === slot.id && a.classroomId !== item.classroomId) {
                conflictTeacherIds.add(a.teacherId);
            }
        });

        // 6) IDs currently assigned in this cell
        const currentHeadId = item.headId;
        const currentInvIds = Array.isArray(item.invigilatorsIds)
            ? item.invigilatorsIds
            : [];

        // 7) Build teacherOptions by filtering out conflicts, except already-assigned here
        const teacherOptions = teachers
            .filter(t => {
                const tid = t.id;
                if (conflictTeacherIds.has(tid)) {
                    if (currentHeadId === tid) return true;
                    if (currentInvIds.includes(tid)) return true;
                    return false;
                }
                return true;
            })
            .map(t => ({
                value: t.id,
                label: `${t.title} ${t.name}`
            }));

        // 8) Find selected options
        const selectedHeadOption = teacherOptions.find(o => o.value === currentHeadId) || null;
        const selectedInvOptions = teacherOptions.filter(o => currentInvIds.includes(o.value));

        return (
            <div className={`cell-content card ${isSaved ? 'border-success' : ''}`}>
                <div className="card-header py-1 px-2 bg-light" style={{ minHeight: "unset" }}>
                    <strong className="small">{item.examName}</strong>
                </div>
                <div className="card-body p-2">
                    {/* Salon Başkanı */}
                    <div className="form-group mb-2">
                        <label className="form-label small mb-1">Salon Başkanı</label>
                        <CustomSelect
                            options={teacherOptions}
                            value={selectedHeadOption}
                            isSearchable
                            onChange={opt => handleInlineUpdate(
                                slot.id,
                                item.classroomId,
                                1,
                                opt ? opt.value : null,
                                currentInvIds
                            )}
                        />
                    </div>
                    {/* Gözetmenler */}
                    <div className="form-group">
                        <label className="form-label small mb-1">Gözetmenler</label>
                        <CustomSelect
                            options={teacherOptions}
                            value={selectedInvOptions}
                            isSearchable
                            isMulti
                            onChange={opts => {
                                const ids = Array.isArray(opts) ? opts.map(o => o.value) : [];
                                handleInlineUpdate(
                                    slot.id,
                                    item.classroomId,
                                    2,
                                    currentHeadId,
                                    ids
                                );
                                setSchedule(prev =>
                                    prev.map(s =>
                                        (s.slotId === slot.id && s.classroomId === item.classroomId && s.roleId === 2)
                                            ? {
                                                ...s,
                                                invigilatorsIds: ids,
                                                invigilators: Array.isArray(opts) ? opts.map(o => o.label) : []
                                            }
                                            : s
                                    )
                                );
                            }}
                        />
                    </div>

                    <div className="text-center mt-2">
                        <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleDownloadPDF(slot.id, item.classroomId)}
                        >
                            PDF İndir
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="container mt-4">
            <h3>Görev Takvimi</h3>

            {/* Dönem Seçimi */}
            <div className="form-group mb-3">
                <h5>Dönem Seç</h5>
                <select
                    className="form-control"
                    value={selectedExamPeriod || ''}
                    onChange={e => setSelectedExamPeriod(Number(e.target.value))}
                >
                    <option value="" disabled>Seçiniz</option>
                    {examPeriods.map(ep =>
                        <option key={ep.id} value={ep.id}>{ep.name}</option>
                    )}
                </select>
            </div>

            {/* Mode tabs */}
            <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                    <button
                        className={`nav-link ${mode === 'teacher' ? 'active' : ''}`}
                        onClick={() => setMode('teacher')}
                    >Öğretim Elemanı</button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${mode === 'classroom' ? 'active' : ''}`}
                        onClick={() => setMode('classroom')}
                    >Sınıf</button>
                </li>
            </ul>

            {/* Building select */}
            {mode === 'classroom' && (
                <div className="form-group mb-3">
                    <select
                        className="form-control"
                        value={selectedBuilding || ''}
                        onChange={e => setSelectedBuilding(Number(e.target.value))}
                    >
                        <option value="" disabled>Bina Seçiniz</option>
                        {buildings.map(b =>
                            <option key={b.id} value={b.id}>{b.name}</option>
                        )}
                    </select>
                </div>
            )}

            {/* Entity select */}
            <div className="form-group mb-3">
                <select
                    className="form-control"
                    value={selectedId || ''}
                    onChange={e => setSelectedId(Number(e.target.value))}
                    disabled={mode === 'classroom' && !selectedBuilding}
                >
                    <option value="" disabled>Seçiniz...</option>
                    {entities.map(ent => (
                        <option key={ent.id} value={ent.id}>
                            {ent.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Week navigation */}
            {weeks.length > 1 && (
                <div className="d-flex justify-content-between mb-2">
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setCurrentWeek(w => Math.max(0, w - 1))}
                        disabled={currentWeek === 0}
                    >&larr; Önceki Hafta</button>
                    <strong>
                        {weeks[currentWeek][0]} – {weeks[currentWeek][6]}
                    </strong>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setCurrentWeek(w => Math.min(weeks.length - 1, w + 1))}
                        disabled={currentWeek === weeks.length - 1}
                    >Sonraki Hafta &rarr;</button>
                </div>
            )}

            {/* Calendar */}
            {selectedId && distinctTimes.length > 0 && weeks.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: `${FIRST_COLUMN}px ${visibleDays.map(() => `${CELL_WIDTH}px`).join(' ')}`,
                        gridAutoRows: mode === 'teacher' ? '80px' : '250px',
                        gap: '1px',
                        background: '#ccc'
                    }}>
                        <div style={{ background: '#f1f1f1' }} />
                        {visibleDays.map(d => (
                            <div key={d} style={{
                                background: '#f1f1f1',
                                textAlign: 'center',
                                padding: '4px'
                            }}>
                                {new Date(d).toLocaleDateString('tr-TR', {
                                    weekday: 'short', day: 'numeric'
                                })}
                            </div>
                        ))}
                        {distinctTimes.map((time, i) => (
                            <React.Fragment key={i}>
                                <div style={{
                                    background: '#f1f1f1',
                                    padding: '4px',
                                    border: '1px solid #bbb',
                                    fontSize: '0.9em'
                                }}>
                                    {time.startTime.substring(0, 5)} - {time.endTime.substring(0, 5)}
                                </div>
                                {visibleDays.map(d =>
                                    <div key={d}>{renderCell(d, time)}</div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
